import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLang } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Eye, EyeOff, Mail, ArrowLeft, CheckCircle2, ChevronRight } from 'lucide-react';
import { isBusiness } from '@/lib/roles';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function formatErr(detail) {
  if (detail == null) return 'Errore. Riprova.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(' ');
  if (detail?.msg) return detail.msg;
  return String(detail);
}

export default function UserAuth() {
  const navigate = useNavigate();
  const { login, completeAuth, user } = useAuth();
  const { t, lang } = useLang();
  const [searchParams] = useSearchParams();
  const initialTab = searchParams.get('tab') || 'login';
  const [tab, setTab] = useState(initialTab);
  const [step, setStep] = useState('form'); // form | verify | forgot | reset | done
  const [pendingEmail, setPendingEmail] = useState('');

  // If already logged in, redirect to dashboard
  useEffect(() => {
    if (user) {
      navigate(isBusiness(user) ? '/restaurant/dashboard' : '/dashboard', { replace: true });
    }
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-white to-amber-50 flex items-center justify-center px-4 py-8" data-testid="user-auth-page">
      <div className="w-full max-w-md">
        <div className="text-center mb-6">
          <button onClick={() => navigate('/')} className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4" data-testid="user-auth-back-home">
            <ArrowLeft className="w-4 h-4" /> {t('back_home')}
          </button>
          <img src="/logo-wordmark@2x.png" alt="Womio" className="h-10 w-auto mx-auto mb-3" />
          <h1 className="text-2xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {step === 'form' && (tab === 'login' ? t('ua_welcome_back') : t('ua_join_womio'))}
            {step === 'verify' && t('ua_verify_title')}
            {step === 'forgot' && t('ua_forgot_title')}
            {step === 'reset' && t('ua_reset_title')}
          </h1>
        </div>

        <div className="bg-white rounded-2xl shadow-xl border border-emerald-100 p-6 sm:p-8">
          {step === 'form' && (
            <FormStep
              tab={tab}
              setTab={setTab}
              onNeedsVerification={(email) => { setPendingEmail(email); setStep('verify'); }}
              onLoginSuccess={(u) => { completeAuth(u); navigate('/dashboard', { replace: true }); }}
              onForgot={() => setStep('forgot')}
              onGoogle={() => login('user')}
              t={t}
              lang={lang}
            />
          )}
          {step === 'verify' && (
            <VerifyStep
              email={pendingEmail}
              onVerified={(u) => { completeAuth(u); navigate('/dashboard', { replace: true }); }}
              onBack={() => setStep('form')}
              lang={lang}
              t={t}
            />
          )}
          {step === 'forgot' && (
            <ForgotStep
              onCodeSent={(email) => { setPendingEmail(email); setStep('reset'); }}
              onBack={() => setStep('form')}
              lang={lang}
              t={t}
            />
          )}
          {step === 'reset' && (
            <ResetStep
              email={pendingEmail}
              onDone={(u) => { completeAuth(u); navigate('/dashboard', { replace: true }); }}
              onBack={() => setStep('forgot')}
              t={t}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------- FORM STEP (login | register) ----------------
function FormStep({ tab, setTab, onNeedsVerification, onLoginSuccess, onForgot, onGoogle, t, lang }) {
  return (
    <>
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="grid grid-cols-2 mb-6 w-full">
          <TabsTrigger value="login" data-testid="ua-tab-login">{t('ua_tab_login')}</TabsTrigger>
          <TabsTrigger value="register" data-testid="ua-tab-register">{t('ua_tab_register')}</TabsTrigger>
        </TabsList>

        <TabsContent value="login">
          <LoginForm onLoginSuccess={onLoginSuccess} onNeedsVerification={onNeedsVerification} onForgot={onForgot} t={t} />
        </TabsContent>
        <TabsContent value="register">
          <RegisterForm onNeedsVerification={onNeedsVerification} lang={lang} t={t} />
        </TabsContent>
      </Tabs>

      <div className="relative my-6">
        <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-gray-200" /></div>
        <div className="relative flex justify-center text-xs uppercase"><span className="bg-white px-2 text-gray-500">{t('ua_or')}</span></div>
      </div>

      <Button variant="outline" className="w-full rounded-full" onClick={onGoogle} data-testid="ua-google-btn">
        <svg className="w-4 h-4 mr-2" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.5 12.3c0-.8-.1-1.5-.2-2.3H12v4.5h6c-.3 1.4-1.1 2.5-2.3 3.3v2.7h3.7c2.2-2 3.4-4.9 3.4-8.2z"/><path fill="#34A853" d="M12 23c3.1 0 5.7-1 7.6-2.8l-3.7-2.9c-1 .7-2.4 1.1-3.9 1.1-3 0-5.6-2-6.5-4.8h-3.8v3c1.9 3.8 5.8 6.4 10.3 6.4z"/><path fill="#FBBC04" d="M5.5 13.6c-.2-.7-.4-1.4-.4-2.1s.1-1.4.4-2.1v-3H1.7C.9 8 .5 9.9.5 12s.4 4 1.2 5.6l3.8-3z"/><path fill="#EA4335" d="M12 5.4c1.7 0 3.2.6 4.4 1.7l3.3-3.3C17.7 1.9 15.1 1 12 1 7.5 1 3.6 3.6 1.7 7.4l3.8 3c.9-2.8 3.5-4.8 6.5-4.8z"/></svg>
        {t('ua_continue_google')}
      </Button>
    </>
  );
}

function LoginForm({ onLoginSuccess, onNeedsVerification, onForgot, t }) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/user/login`, { email, password }, { withCredentials: true });
      toast.success(t('ua_login_ok'));
      onLoginSuccess(res.data);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const status = err?.response?.status;
      if (status === 403 && typeof detail === 'string' && detail.toLowerCase().includes('non verificat')) {
        // Trigger resend + go to verify step
        toast.info(t('ua_need_verify'));
        try {
          await axios.post(`${API}/auth/user/resend-verification`, { email }, { withCredentials: true });
        } catch (_) {}
        onNeedsVerification(email);
      } else {
        setError(formatErr(detail));
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="ua-login-form">
      <div>
        <Label htmlFor="login-email">{t('ua_email')}</Label>
        <Input id="login-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="mario@example.com" data-testid="ua-login-email" autoComplete="email" />
      </div>
      <div>
        <Label htmlFor="login-password">{t('ua_password')}</Label>
        <div className="relative">
          <Input id="login-password" type={showPw ? 'text' : 'password'} required value={password} onChange={(e) => setPassword(e.target.value)} data-testid="ua-login-password" autoComplete="current-password" />
          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" tabIndex={-1}>
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {error && <p className="text-sm text-destructive" data-testid="ua-login-error">{error}</p>}
      <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="ua-login-submit">
        {loading ? t('ua_loading') : t('ua_login_btn')} {!loading && <ChevronRight className="w-4 h-4 ml-1" />}
      </Button>
      <button type="button" onClick={onForgot} className="text-sm text-primary hover:underline block mx-auto" data-testid="ua-forgot-link">
        {t('ua_forgot_link')}
      </button>
    </form>
  );
}

function RegisterForm({ onNeedsVerification, lang, t }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (password !== confirm) {
      setError(t('ua_pw_mismatch'));
      return;
    }
    if (password.length < 8) {
      setError(t('ua_pw_too_short'));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/auth/user/register`, { name, email, password, password_confirm: confirm, lang }, { withCredentials: true });
      toast.success(t('ua_registered_check_email'));
      onNeedsVerification(email);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="ua-register-form">
      <div>
        <Label htmlFor="reg-name">{t('ua_name')}</Label>
        <Input id="reg-name" type="text" required value={name} onChange={(e) => setName(e.target.value)} placeholder="Mario Rossi" data-testid="ua-register-name" autoComplete="name" />
      </div>
      <div>
        <Label htmlFor="reg-email">{t('ua_email')}</Label>
        <Input id="reg-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="mario@example.com" data-testid="ua-register-email" autoComplete="email" />
      </div>
      <div>
        <Label htmlFor="reg-password">{t('ua_password')} <span className="text-xs text-muted-foreground">({t('ua_pw_hint')})</span></Label>
        <div className="relative">
          <Input id="reg-password" type={showPw ? 'text' : 'password'} required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)} data-testid="ua-register-password" autoComplete="new-password" />
          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" tabIndex={-1}>
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <Label htmlFor="reg-confirm">{t('ua_password_confirm')}</Label>
        <Input id="reg-confirm" type={showPw ? 'text' : 'password'} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="ua-register-confirm" autoComplete="new-password" />
        {confirm && confirm !== password && (
          <p className="text-xs text-destructive mt-1" data-testid="ua-pw-mismatch-hint">{t('ua_pw_mismatch')}</p>
        )}
      </div>
      {error && <p className="text-sm text-destructive" data-testid="ua-register-error">{error}</p>}
      <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="ua-register-submit">
        {loading ? t('ua_loading') : t('ua_register_btn')} {!loading && <ChevronRight className="w-4 h-4 ml-1" />}
      </Button>
      <p className="text-xs text-muted-foreground text-center">{t('ua_terms_notice')}</p>
    </form>
  );
}

// ---------------- VERIFY STEP ----------------
function VerifyStep({ email, onVerified, onBack, lang, t }) {
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [resendCooldown, setResendCooldown] = useState(60);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setTimeout(() => setResendCooldown((c) => c - 1), 1000);
    return () => clearTimeout(timer);
  }, [resendCooldown]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (code.length !== 6) return;
    setError('');
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/user/verify-email`, { email, code }, { withCredentials: true });
      toast.success(t('ua_verify_ok'));
      onVerified(res.data);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (resendCooldown > 0) return;
    try {
      await axios.post(`${API}/auth/user/resend-verification`, { email, lang }, { withCredentials: true });
      toast.success(t('ua_resend_ok'));
      setResendCooldown(60);
    } catch (err) {
      toast.error(formatErr(err?.response?.data?.detail));
    }
  };

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="ua-verify-form">
      <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
        <Mail className="w-5 h-5 text-emerald-700 flex-shrink-0" />
        <p className="text-sm text-emerald-900">{t('ua_verify_sent')} <b>{email}</b></p>
      </div>
      <div>
        <Label className="mb-2 block">{t('ua_enter_code')}</Label>
        <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="ua-verify-otp">
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => (<InputOTPSlot key={i} index={i} />))}
          </InputOTPGroup>
        </InputOTP>
      </div>
      {error && <p className="text-sm text-destructive" data-testid="ua-verify-error">{error}</p>}
      <Button type="submit" className="w-full rounded-full" disabled={loading || code.length !== 6} data-testid="ua-verify-submit">
        {loading ? t('ua_loading') : (<><CheckCircle2 className="w-4 h-4 mr-1" /> {t('ua_verify_btn')}</>)}
      </Button>
      <div className="flex items-center justify-between text-sm">
        <button type="button" onClick={onBack} className="text-muted-foreground hover:text-foreground" data-testid="ua-verify-back">← {t('back')}</button>
        <button type="button" onClick={resend} disabled={resendCooldown > 0} className="text-primary hover:underline disabled:opacity-40 disabled:no-underline" data-testid="ua-verify-resend">
          {resendCooldown > 0 ? `${t('ua_resend_in')} ${resendCooldown}s` : t('ua_resend')}
        </button>
      </div>
    </form>
  );
}

// ---------------- FORGOT STEP ----------------
function ForgotStep({ onCodeSent, onBack, lang, t }) {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/user/forgot-password`, { email, lang }, { withCredentials: true });
      toast.success(t('ua_forgot_sent'));
      onCodeSent(email);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="ua-forgot-form">
      <p className="text-sm text-muted-foreground">{t('ua_forgot_sub')}</p>
      <div>
        <Label htmlFor="forgot-email">{t('ua_email')}</Label>
        <Input id="forgot-email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="mario@example.com" data-testid="ua-forgot-email" />
      </div>
      {error && <p className="text-sm text-destructive" data-testid="ua-forgot-error">{error}</p>}
      <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="ua-forgot-submit">
        {loading ? t('ua_loading') : t('ua_forgot_btn')}
      </Button>
      <button type="button" onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground block mx-auto" data-testid="ua-forgot-back">
        ← {t('back')}
      </button>
    </form>
  );
}

// ---------------- RESET STEP ----------------
function ResetStep({ email, onDone, onBack, t }) {
  const [code, setCode] = useState('');
  const [newPw, setNewPw] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const submit = async (e) => {
    e.preventDefault();
    setError('');
    if (newPw !== confirm) { setError(t('ua_pw_mismatch')); return; }
    if (newPw.length < 8) { setError(t('ua_pw_too_short')); return; }
    if (code.length !== 6) { setError(t('ua_code_invalid')); return; }
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/user/reset-password`, { email, code, new_password: newPw, new_password_confirm: confirm }, { withCredentials: true });
      toast.success(t('ua_reset_ok'));
      onDone(res.data);
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={submit} className="space-y-4" data-testid="ua-reset-form">
      <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-lg border border-emerald-200">
        <Mail className="w-5 h-5 text-emerald-700 flex-shrink-0" />
        <p className="text-sm text-emerald-900">{t('ua_reset_sent')} <b>{email}</b></p>
      </div>
      <div>
        <Label className="mb-2 block">{t('ua_enter_code')}</Label>
        <InputOTP maxLength={6} value={code} onChange={setCode} data-testid="ua-reset-otp">
          <InputOTPGroup>
            {[0, 1, 2, 3, 4, 5].map((i) => (<InputOTPSlot key={i} index={i} />))}
          </InputOTPGroup>
        </InputOTP>
      </div>
      <div>
        <Label htmlFor="reset-newpw">{t('ua_new_password')}</Label>
        <div className="relative">
          <Input id="reset-newpw" type={showPw ? 'text' : 'password'} required minLength={8} value={newPw} onChange={(e) => setNewPw(e.target.value)} data-testid="ua-reset-newpw" autoComplete="new-password" />
          <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground" tabIndex={-1}>
            {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <div>
        <Label htmlFor="reset-confirm">{t('ua_password_confirm')}</Label>
        <Input id="reset-confirm" type={showPw ? 'text' : 'password'} required value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="ua-reset-confirm" autoComplete="new-password" />
      </div>
      {error && <p className="text-sm text-destructive" data-testid="ua-reset-error">{error}</p>}
      <Button type="submit" className="w-full rounded-full" disabled={loading} data-testid="ua-reset-submit">
        {loading ? t('ua_loading') : t('ua_reset_btn')}
      </Button>
      <button type="button" onClick={onBack} className="text-sm text-muted-foreground hover:text-foreground block mx-auto" data-testid="ua-reset-back">
        ← {t('back')}
      </button>
    </form>
  );
}
